import React, { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { applyServiceWorkerUpdate } from "@/lib/registerSW";

/**
 * Slides in from the bottom when a new service worker version has finished
 * installing. The user picks their moment to refresh — no forced logouts,
 * no dropped forms. Dismiss button hides it for the current session only.
 */
export default function AppUpdateBanner() {
  const [registration, setRegistration] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onUpdate = (e) => {
      const reg = e?.detail?.registration;
      if (reg) setRegistration(reg);
    };
    window.addEventListener("sw-update-available", onUpdate);
    return () => window.removeEventListener("sw-update-available", onUpdate);
  }, []);

  if (!registration || dismissed) return null;

  const onRefresh = () => {
    applyServiceWorkerUpdate(registration);
  };

  return (
    <div
      data-testid="app-update-banner"
      className="fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4 pointer-events-none"
    >
      <div className="pointer-events-auto flex items-center gap-3 rounded-full border-2 border-[#0F172A] bg-[#0F172A] text-white shadow-2xl pl-5 pr-2 py-2 max-w-md w-full sm:w-auto animate-in slide-in-from-bottom-4">
        <RefreshCw className="w-4 h-4 text-[#F97316] shrink-0" />
        <div className="flex-1 text-sm">
          <div className="font-semibold tracking-wide">App updated</div>
          <div className="text-[11px] text-slate-300">
            A newer version is ready. Refresh when convenient.
          </div>
        </div>
        <button
          data-testid="app-update-refresh-btn"
          onClick={onRefresh}
          className="bg-[#F97316] hover:bg-[#EA6A0C] text-white text-xs font-bold tracking-wider uppercase px-4 py-2 rounded-full transition-colors"
        >
          Refresh
        </button>
        <button
          data-testid="app-update-dismiss-btn"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss update banner"
          className="text-slate-400 hover:text-white p-1 rounded-full transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
